// web/src/hooks/useBTCPrice.js
import { useState, useEffect, useCallback } from 'react';
import { PriceAPI } from '../services/api';

export const useBTCPrice = (refreshInterval = 60000) => {
    const [price, setPrice] = useState(null);
    const [formattedPrice, setFormattedPrice] = useState('$0.00');
    const [lastUpdated, setLastUpdated] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchPrice = useCallback(async (forceRefresh = false) => {
        try {
            setLoading(true);
            const response = await PriceAPI.getBTCPriceWithCache(forceRefresh);
            
            if (response.success) {
                setPrice(response.data.btc_usd);
                setFormattedPrice(response.data.btc_usd_formatted);
                setLastUpdated(response.data.last_updated);
                setError(null);
            } else {
                setError(response.error || 'Failed to fetch BTC price');
            }
        } catch (err) {
            setError(err.message || 'Failed to fetch BTC price');
        } finally {
            setLoading(false);
        }
    }, []);

    // Convert BTC to USD
    const btcToUsd = useCallback((btcAmount) => {
        if (!price) return 0;
        return btcAmount * price;
    }, [price]);

    // Convert USD to BTC
    const usdToBtc = useCallback((usdAmount) => {
        if (!price) return 0;
        return usdAmount / price;
    }, [price]);

    // Format amount based on current currency preference
    const formatAmount = useCallback((btcAmount, showBoth = false) => {
        if (!price) return `${btcAmount.toFixed(8)} BTC`;
        
        const usdAmount = btcAmount * price;
        if (showBoth) {
            return `$${usdAmount.toFixed(2)} (≈${btcAmount.toFixed(8)} BTC)`;
        }
        return `$${usdAmount.toFixed(2)}`;
    }, [price]);

    // Initial fetch
    useEffect(() => {
        fetchPrice();
        
        // Refresh interval
        const interval = setInterval(() => {
            fetchPrice(true);
        }, refreshInterval);
        
        return () => clearInterval(interval);
    }, [fetchPrice, refreshInterval]);

    return {
        price,
        formattedPrice,
        lastUpdated,
        loading,
        error,
        fetchPrice,
        btcToUsd,
        usdToBtc,
        formatAmount,
        // Convenience method for refreshing
        refresh: () => fetchPrice(true)
    };
};

export default useBTCPrice;